import type { LeadSubmission } from '@shared/types.js';

/**
 * Funnel beacon — the only two events the server cannot observe by itself.
 *
 * Sends a single allow-listed event name and nothing else: no id, no cookie,
 * no page data, no timing. Fire-and-forget, and failures are swallowed so
 * telemetry can never affect the visitor's flow.
 */
export function trackFunnel(event: 'scan_started' | 'scan_completed'): void {
  void fetch('/api/track', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ event }),
    keepalive: true,
  }).catch(() => {
    /* never surface telemetry problems to the visitor */
  });
}

export interface SubmitLeadResponse {
  /** True when a confirmation email was sent and the Scorecard is held back. */
  awaitingConfirmation: boolean;
  emailSentTo: string | null;
  expiresInHours: number;
  /** Only populated when double opt-in is switched off. */
  scorecardUrl: string | null;
}

export interface ConfirmResponse {
  result: import('@shared/types.js').ScanResult;
  scorecardUrl: string;
  company: string;
}

/** Redeems the emailed confirmation token. Single use. */
export async function confirmLead(token: string): Promise<ConfirmResponse> {
  const response = await fetch('/api/confirm-lead', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      /* keep status-based message */
    }
    throw new Error(message);
  }
  return (await response.json()) as ConfirmResponse;
}

/**
 * Posts the lead to the Functions API.
 *
 * The browser sends the ANSWERS, not the score: the server re-derives the NDSS
 * from the same shared config so a tampered client cannot fake a result, and so
 * the stored record and the PDF always agree. Tiering, the Table Storage write
 * and the Power Automate webhook all happen server-side and are never exposed
 * here (build prompt §5, §8).
 */
export async function submitLead(submission: LeadSubmission): Promise<SubmitLeadResponse> {
  const response = await fetch('/api/submit-lead', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(submission),
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // Non-JSON error body — keep the status-based message.
    }
    throw new Error(message);
  }

  return (await response.json()) as SubmitLeadResponse;
}
